/**
 * MachineDonutChart Molecule
 * Donut chart showing machine connection status breakdown
 */

import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/core/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/core/components/ui/tooltip';
import { formatNumber, formatPercentage } from '../../utils/currency-utils';

interface MachineStatusBreakdown {
  status: string;
  count: number;
  percentage: number;
  color: string;
}

interface MachineDonutChartProps {
  onlineMachines: number;
  disconnectedMachines: number;
  totalMachines: number;
  isLoading?: boolean;
  title?: string;
}

export function MachineDonutChart({
  onlineMachines,
  disconnectedMachines,
  totalMachines,
  isLoading = false,
  title = 'Machine Connection Status',
}: MachineDonutChartProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-48 flex items-center justify-center">
            <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (totalMachines === 0) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-48 flex items-center justify-center text-gray-500">
            No machine data available
          </div>
        </CardContent>
      </Card>
    );
  }

  const data: MachineStatusBreakdown[] = [
    {
      status: 'Online',
      count: onlineMachines,
      percentage: (onlineMachines / totalMachines) * 100,
      color: '#1d4ed8', // blue-700
    },
    {
      status: 'Disconnected',
      count: disconnectedMachines,
      percentage: (disconnectedMachines / totalMachines) * 100,
      color: '#ef4444', // red-500
    },
  ];

  const chartData = data.map(item => ({
    ...item,
    percentage: Math.round(item.percentage * 10) / 10,
  }));

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-xs md:text-sm font-medium text-black">{title}</CardTitle>
      </CardHeader>
      <CardContent className="p-2 md:p-4">
        <div className="flex items-start gap-5 md:gap-6">
          {/* Left side - Donut chart */}
          <div className="flex-shrink-0 w-20 h-20 md:w-24 md:h-24 relative">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={25}
                  outerRadius={38}
                  paddingAngle={2}
                  dataKey="count"
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <RechartsTooltip
                  position={{ x: 0, y: 100 }}
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      return (
                        <div className="bg-white p-2 border border-purple-300 rounded shadow-lg">
                          <p className="font-medium text-black text-xs md:text-sm">{data.status}</p>
                          <p className="text-xs md:text-sm text-black">
                            {formatNumber(data.count, 0)} ({formatPercentage(data.percentage)})
                          </p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
            {/* Center total - positioned inside the donut */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="text-center">
                <div className="text-base md:text-lg font-bold text-black">{formatNumber(totalMachines, 0)}</div>
                <div className="text-[8px] md:text-[9px] text-black">Total</div>
              </div>
            </div>
          </div>

          {/* Right side - Legend */}
          <TooltipProvider>
            <div className="flex-1 flex flex-col gap-0.5 md:gap-1 justify-center">
              {chartData.map((item, index) => (
                <Tooltip key={index} delayDuration={300}>
                  <TooltipTrigger asChild>
                    <div className="flex items-center gap-1 md:gap-1.5 rounded px-1 md:px-1.5 py-0.5">
                      <div
                        className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full flex-shrink-0"
                        style={{ backgroundColor: item.color }}
                      />
                      <span className="text-[10px] md:text-xs font-medium flex-shrink-0" style={{ color: '#000 !important', opacity: 1 }}>
                        {item.status}:
                      </span>
                      <span className="text-[10px] md:text-xs font-semibold ml-auto flex-shrink-0 tabular-nums" style={{ color: '#000 !important', opacity: 1 }}>
                        {formatNumber(item.count, 0)}
                      </span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <div className="text-center">
                      <p className="font-medium text-black text-xs md:text-sm">{item.status}</p>
                      <p className="text-xs md:text-sm text-black">
                        {formatNumber(item.count, 0)} machines ({formatPercentage(item.percentage)})
                      </p>
                    </div>
                  </TooltipContent>
                </Tooltip>
              ))}
            </div>
          </TooltipProvider>
        </div>
      </CardContent>
    </Card>
  );
}
